import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { MultiDomainSelector } from '@/components/shared/MultiDomainSelector';
import { extractContentFromFile } from '@/utils/universalContentExtractor';
import { useUserChannels } from '@/hooks/useUserChannels';

interface KnowledgeBaseUploadProps {
  domainId?: string;
  channelId?: string;
  isBounty?: boolean;
  bountyAmount?: number;
  onSuccess?: (knowledgeBaseId: string) => void;
}

export const KnowledgeBaseUpload = ({ 
  domainId,
  channelId,
  isBounty = false, 
  bountyAmount,
  onSuccess 
}: KnowledgeBaseUploadProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(channelId ? [channelId] : []);
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>(domainId ? [domainId] : []);
  const [showNewDomainForm, setShowNewDomainForm] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainSymbol, setNewDomainSymbol] = useState('');
  const [newDomainDescription, setNewDomainDescription] = useState('');

  // Fetch user's channel assignments
  const { data: userChannels } = useUserChannels(user?.id);

  // Fetch available channels (only those assigned to user)
  const { data: channels } = useQuery({
    queryKey: ['user-allowed-channels', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Get channels the user is assigned to
      const { data, error } = await supabase
        .from('user_organizations')
        .select('channels(id, name)')
        .eq('user_id', user.id);
      
      if (error) throw error;
      return data?.map(uo => uo.channels).filter(Boolean) || [];
    },
    enabled: !!user?.id
  });

  // Fetch available domains
  const { data: domains } = useQuery({
    queryKey: ['domains'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domains')
        .select('id, name, symbol, description')
        .order('name');
      
      if (error) throw error;
      return data;
    }
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Auto-extract metadata from document
      await extractMetadata(selectedFile);
    }
  };

  const extractMetadata = async (file: File) => {
    setExtracting(true);
    try {
      // Read file content
      const text = await readFileAsText(file);
      
      // Call edge function to extract metadata
      const { data, error } = await supabase.functions.invoke('parse-document-metadata', {
        body: { 
          fileContent: text,
          fileName: file.name
        }
      });

      if (error) throw error;

      if (data?.title && !title) {
        setTitle(data.title);
      }
      if (data?.description && !description) {
        setDescription(data.description);
      }

      toast({
        title: "Metadata extracted",
        description: "Title and description have been auto-populated from your document"
      });
    } catch (error: any) {
      console.error('Metadata extraction error:', error);
      // Don't show error toast - just fall back to manual entry
    } finally {
      setExtracting(false);
    }
  };

  const readFileAsText = async (file: File): Promise<string> => {
    const { content } = await extractContentFromFile(file);
    return content;
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCoverImage(e.target.files[0]);
    }
  };

  const handleCreateDomain = async () => {
    if (!newDomainName || !newDomainSymbol) {
      toast({
        title: "Missing information",
        description: "Please provide domain name and symbol",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('domains')
        .insert({
          name: newDomainName,
          symbol: newDomainSymbol,
          description: newDomainDescription
        })
        .select()
        .single();

      if (error) throw error;

      setSelectedDomainIds([data.id]);
      setShowNewDomainForm(false);
      setNewDomainName('');
      setNewDomainSymbol('');
      setNewDomainDescription('');
      toast({
        title: "Domain created",
        description: `"${newDomainName}" has been added`
      });
    } catch (error: any) {
      toast({
        title: "Failed to create domain",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUpload = async () => {
    if (!user || !title || !file) {
      toast({
        title: "Missing information",
        description: "Please provide a title and select a file",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('knowledge-bases')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL for file
      const { data: { publicUrl } } = supabase.storage
        .from('knowledge-bases')
        .getPublicUrl(fileName);

      // Upload cover image if provided
      let coverImageUrl: string | null = null;
      if (coverImage) {
        const coverExt = coverImage.name.split('.').pop();
        const coverFileName = `covers/${user.id}/${Date.now()}.${coverExt}`;
        
        const { error: coverUploadError } = await supabase.storage
          .from('knowledge-bases')
          .upload(coverFileName, coverImage);

        if (coverUploadError) throw coverUploadError;

        const { data: { publicUrl: coverUrl } } = supabase.storage
          .from('knowledge-bases')
          .getPublicUrl(coverFileName);
        
        coverImageUrl = coverUrl;
      }

      // Create knowledge base record - everything is public now
      const { data: kbData, error: kbError } = await supabase
        .from('knowledge_bases')
        .insert({
          creator_id: user.id,
          title,
          description,
          file_url: publicUrl,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          channel_id: channelId || null,
          is_bounty: isBounty,
          bounty_amount: bountyAmount || null,
          nft_cover_image_url: coverImageUrl,
          selected_channels: selectedChannels,
          confirmation_status: 'confirmed',
          status: 'published',
          published_at: new Date().toISOString(),
          confirmed_at: new Date().toISOString()
        })
        .select()
        .single();

      if (kbError) throw kbError;

      // Create domain associations
      if (selectedDomainIds.length > 0) {
        try {
          const domainAssociations = selectedDomainIds.map(domainId => ({
            knowledge_base_id: kbData.id,
            domain_id: domainId
          }));
          
          const { error: domainError } = await supabase
            .from('knowledge_base_domains')
            .insert(domainAssociations);
            
          if (domainError) {
            console.error('Domain association error:', domainError);
            throw new Error(`Failed to associate domains: ${domainError.message}`);
          }
        } catch (error: any) {
          console.error('Domain association failed:', error);
          throw error;
        }
      }

      // Create attribution record
      try {
        const { error: attrError } = await supabase
          .from('attributions')
          .insert({
            knowledge_base_id: kbData.id,
            user_id: user.id,
            contribution_type: 'creator',
            ownership_percentage: 100
          });

        if (attrError) {
          console.error('Attribution error:', attrError);
          throw new Error(`Failed to create attribution: ${attrError.message}`);
        }
      } catch (error: any) {
        console.error('Attribution creation failed:', error);
        throw error;
      }

      // Grant access to creator
      await supabase
        .from('kb_access')
        .insert({
          knowledge_base_id: kbData.id,
          user_id: user.id,
          access_level: 'owner',
          granted_by: user.id
        });

      toast({
        title: "Success!",
        description: "Your knowledge base has been submitted for confirmation"
      });

      if (onSuccess) {
        onSuccess(kbData.id);
      }

      // Reset form
      setTitle('');
      setDescription('');
      setFile(null);
      setCoverImage(null);
      setSelectedChannels(channelId ? [channelId] : []);
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter knowledge base title"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your knowledge contribution"
          rows={4}
        />
      </div>

      <MultiDomainSelector
        channelId={channelId}
        selectedDomainIds={selectedDomainIds}
        onDomainsChange={setSelectedDomainIds}
        maxSelections={10}
        label="Select Domains"
      />

      <div className="space-y-2">
        <Label htmlFor="coverImage">NFT Cover Image (Optional)</Label>
        <Input
          id="coverImage"
          type="file"
          onChange={handleCoverImageChange}
          accept="image/*"
        />
        {coverImage && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            {coverImage.name} ({(coverImage.size / 1024).toFixed(2)} KB)
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="file">Upload File</Label>
        <Input
          id="file"
          type="file"
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.txt,.md"
          disabled={extracting}
        />
        {extracting && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Extracting title and description from document...
          </p>
        )}
        {file && !extracting && (
          <p className="text-sm text-muted-foreground">
            Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
          </p>
        )}
      </div>

      <Button 
        onClick={handleUpload} 
        disabled={uploading || !title || !file || selectedDomainIds.length === 0}
        className="w-full"
      >
        {uploading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            Create Knowledge Base
          </>
        )}
      </Button>
    </div>
  );
};
